import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Renderer2,
  NgZone,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';

type AllowedEndpoints =
  | 'get_inventory'
  | 'get_products'
  | 'create_product'
  | 'update_product'
  | 'get_inbound_shipment'
  | 'create_inbound_shipment'
  | 'update_inbound_shipment'
  | 'get_orders'
  | 'create_order'
  | 'update_order'
  | 'create_kit'
  | 'update_kit'
  | 'get_returns'
  | 'create_return'
  | 'update_return'
  | 'get_shipping_methods'
  | 'get_bills'
  | 'get_warehouses'
  | 'get_warehouse_customers'
  | 'get_labor_activities';

interface ClientConfig {
  onSuccess: (authCode: string) => void;
  onClose?: (error?: any) => void;
  onLoad?: () => void;
  getLinkToken: () => Promise<string>;
  integrationAllowList?: string[];
  integrationBlockList?: string[];
  integrationsWithEndpoints?: AllowedEndpoints[];
  buttonId?: string;
  sandbox?: boolean;
}

declare global {
  interface Window {
    Trackstar: any;
  }
}

export enum TrackstarIntegrationName {
  Extensiv_3PL_Central = 'Extensiv-3PL-Central',
  NFI_3PL = 'NFI-3PL',
  Amazon_Mcf_3PL = 'Amazon-MCF',
  STACI_AMERICA = 'Staci-America',
  CAPACITY = 'Capacity',
}

export enum ExternalIntegrationName {
  Extensiv_3PL_Central = 'extensiv-3pl-central',
  NFI_3PL = 'nfi',
  Amazon_Mcf_3PL = 'amazon',
  STACI_AMERICA = 'staci-america',
  CAPACITY = 'capacity',
}

export const InternalInegrationNameToTrackstarNameMap: {
  [key in TrackstarIntegrationName]: ExternalIntegrationName;
} = {
  [TrackstarIntegrationName.Extensiv_3PL_Central]: ExternalIntegrationName.Extensiv_3PL_Central,
  [TrackstarIntegrationName.NFI_3PL]: ExternalIntegrationName.NFI_3PL,
  [TrackstarIntegrationName.Amazon_Mcf_3PL]: ExternalIntegrationName.Amazon_Mcf_3PL,
  [TrackstarIntegrationName.STACI_AMERICA]: ExternalIntegrationName.STACI_AMERICA,
  [TrackstarIntegrationName.CAPACITY]: ExternalIntegrationName.CAPACITY,
};

const integrationsConfig = {
  [TrackstarIntegrationName.Extensiv_3PL_Central.toLowerCase()]: {
    fields: {
      client_id: 'input[name="client_id"]',
      client_secret: 'input[name="client_secret"]',
      user_login_id: 'input[name="user_login_id"]',
    },
  },
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.NFI_3PL]]: {
    fields: {
      sender_id: 'input[name="sender_id"]',
      access_token: 'input[name="access_token"]',
    },
  },
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.STACI_AMERICA]]: {
    fields: {
      client_id: 'input[name="client_id"]',
      user_login_id: 'input[name="client_identifier"]',
    },
  },
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.CAPACITY]]: {
    fields: {
      client_id: 'input[name="client_id"]',
      client_secret: 'input[name="client_secret"]',
      client_key: 'input[name="client_key"]',
      client_code: 'input[name="client_code"]',
    },
  },
  // AnotherIntegration: {
  //   fields: {
  //     bearer_token: 'input[name="bearer_token"]',
  //   },
  // },
  // Add more integrations as needed
};

@Component({
  selector: 'trackstar-component',
  templateUrl: './trackstar-component.component.html',
  styleUrls: ['./trackstar-component.component.scss'],
  standalone: false,
})
export class TrackstarConnectButtonComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: ClientConfig;
  @Input() buttonText: string = 'Connect WMS';
  @Input() buttonStyle: any;
  @Input() buttonClass: string;
  @Input() integrationName: string;
  @Input() credentialsConfiguration: any;
  @Input() openDialogAfterInit: boolean;
  @Input() doNotHideModal: boolean;
  @Input() isSandBox: boolean;

  @Output() onClick = new EventEmitter<void>();
  @Output() printErrors = new EventEmitter<any>();

  error: any;
  loading: boolean = true;
  trackstarModalId: string;
  modalClosed: boolean = false;
  errorObject: { errorMsg1: string; errorMsg2: string };
  successObject: { successMsg: string };
  closeButton;
  observer: MutationObserver;
  trackstarContatinerFound: boolean = false;
  isContinueButtonClicked: boolean = false;

  constructor(
    private renderer: Renderer2,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.initObserver();
    this.trackstarModalId = this.config.hasOwnProperty('buttonId') ? 'Trackstar' + this.config.buttonId : 'Trackstar';
    this.loadScript('https://link.trackstarhq.com/main.js')
      .then(() => {
        this.loading = false;
        if (window.Trackstar) {
          window.Trackstar.init({
            ...this.config,
            onLoad: () => {
              this.config.onLoad && this.config.onLoad();
              if (this.openDialogAfterInit) {
                this.open();
              }
            },
            onClose: () => this.config.onClose && this.config.onClose(),
            getLinkToken: () => this.config.getLinkToken && this.config.getLinkToken(),
          });
        }
      })
      .catch((err) => {
        this.loading = false;
        this.error = err;
      });
  }

  ngAfterViewInit(): void {
    this.checkAndOpenDialog();
  }

  ngOnDestroy() {
    this.modalClosed = true;
    this.observer?.disconnect();
    this.closeButton?.click();
  }

  initObserver() {
    // Trackstar modal is rendered in the body and we want to hide it and simulate the continue button click
    const body = document.querySelector('body');
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if ((mutation.target as HTMLElement)?.id == 'trackstar-container') {
          this.trackstarContatinerFound = true;
          if (!this.isContinueButtonClicked) {
            if (!this.doNotHideModal) {
              this.hideModal(mutation.target);
            }
            this.simulateContinueButtonClick(mutation);
          } else {
            setTimeout(() => {
              let listComponent;
              mutation?.addedNodes.forEach((node) => {
                if (node.textContent?.startsWith('Select your WMS')) {
                  listComponent = node;
                  this.simulateIntegrationClick(listComponent);
                } else if (node.childNodes?.length > 2 && node.childNodes[2].firstChild instanceof HTMLFormElement) {
                  this.injectFormValues(node as HTMLElement);
                } else if (
                  node.childNodes?.length > 1 &&
                  node?.childNodes?.[1]?.childNodes?.[1]?.childNodes?.[0] instanceof HTMLSelectElement
                ) {
                  this.injectSelectValues(node as HTMLElement);
                }
              });
            }, 200);
          }
        }
        // if the continue button is not found or is disabled, keep observing
        if (
          this.trackstarContatinerFound &&
          !this.isContinueButtonClicked &&
          mutation.target instanceof HTMLButtonElement &&
          mutation.target.textContent.toLowerCase() === 'continue'
        ) {
          console.log('Continue button found:', mutation.target);
          if (!this.isContinueButtonClicked && !mutation.target.disabled) {
            console.log('Continue button is clickable');
            mutation.target.click();
            this.isContinueButtonClicked = true;
            this.observer.disconnect();
          } else {
            console.log('Continue button is not clickable yet, retrying...');
          }
        }
      });
    });
    this.zone.runOutsideAngular(() => {
      this.observer.observe(body, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    });
  }

  loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () =>
        setTimeout(() => {
          resolve();
        });
      script.onerror = () => reject(`Error loading script: ${src}`);
      document.body.appendChild(script);
    });
  }

  open() {
    if (this.error) {
      throw new Error(`Error loading Trackstar script: ${this.error}`);
    }
    if (!window[this.trackstarModalId]) {
      console.error('Trackstar is not initialized');
      return;
    }
    if (!window[this.trackstarModalId].state?.isLoaded) {
      console.error('Trackstar has not been loaded, did you call Trackstar.init()?');
      return;
    }
    window[this.trackstarModalId].open({ integrationId: undefined });
  }

  checkAndOpenDialog(): void {
    // Wait until Trackstar is fully initialized before attempting to open the dialog
    const interval = setInterval(() => {
      if (window.Trackstar && window.Trackstar.state?.isLoaded) {
        clearInterval(interval);
        if (this.openDialogAfterInit) {
          this.open();
        }
      }
    }, 500);

    // Stop checking after 15 seconds
    setTimeout(() => clearInterval(interval), 15000);
  }

  handleClick() {
    this.onClick.emit();
    this.open();
  }
  simulateContinueButtonClick(mutation?: MutationRecord) {
    if (mutation) {
      console.log('Start simulate continue button click');
      this.observer.disconnect();
      this.closeButton = mutation.nextSibling;
      const integrationComponent = this.closeButton?.nextElementSibling;
      const continueButton =
        integrationComponent?.childNodes &&
        (Array.from(integrationComponent.childNodes)?.find(
          (child) => child instanceof HTMLButtonElement && child.textContent.toLowerCase() === 'continue',
        ) as HTMLButtonElement);
      if (continueButton && !continueButton.disabled) {
        if (this.isContinueButtonClicked) {
          return;
        }
        console.log('Continue button is clickable');
        this.observeTrackstarContainer(mutation.target as HTMLElement);
        continueButton.click();
        this.isContinueButtonClicked = true;
      } else {
        console.log(`Continue button is not found or is disabled, retrying...`);
      }
    }
  }

  observeTrackstarContainer(container: HTMLElement) {
    this.observer.disconnect();
    this.observer.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  simulateIntegrationClick(listComponent: Node) {
    let integrationListButtons: ChildNode;
    Array.from(listComponent?.childNodes)?.find((child) => {
      integrationListButtons = Array.from(child.childNodes)?.find((child2) =>
        (child2 as HTMLElement).classList?.contains('MuiList-root'),
      );
      return !!integrationListButtons;
    });
    integrationListButtons =
      integrationListButtons ?? ((listComponent as HTMLElement).querySelector('.MuiList-root') as HTMLElement);

    const currentIntegration =
      integrationListButtons?.childNodes &&
      (Array.from(integrationListButtons.childNodes).find(
        (child) =>
          (child as any)?.textContent?.trim().toLowerCase().split(' ')?.[0] ===
          this.integrationName?.split('-')?.[0].toLowerCase(),
      ) as HTMLButtonElement);
    if (currentIntegration) {
      console.log(`Found current integration ${currentIntegration.textContent}`);
      currentIntegration.click();
    }
  }

  injectFormValues(formComponent?: HTMLElement) {
    const integrationConfig = integrationsConfig[this.integrationName];
    const fields = {};
    let allFieldsInjected = true;
    for (const [fieldName, selector] of Object.entries(integrationConfig.fields)) {
      fields[fieldName] = document.querySelector(selector as string) as HTMLInputElement;
    }

    const submitButton = formComponent?.querySelector('button[type="submit"]') as HTMLButtonElement;
    for (const [fieldName, inputElement] of Object.entries(fields)) {
      if (inputElement) {
        this.simulateUserInput(inputElement as HTMLInputElement, this.credentialsConfiguration?.[fieldName]);
      } else {
        allFieldsInjected = false;
        console.log(`Field ${fieldName} not found, retrying...`);
      }
    }

    if (allFieldsInjected && submitButton) {
      console.log('Form values injected');
      if (this.isSandBox) {
        // Trigger a click event on the checkbox input
        const checkboxInput = formComponent.querySelector('input[type="checkbox"]');
        if (checkboxInput) {
          this.renderer.setProperty(checkboxInput, 'checked', true);
        } else {
          console.log('Checkbox not found for testMode');
        }
      }

      setTimeout(() => {
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('Mui-disabled');
        if (!submitButton.hasAttribute('disabled')) {
          submitButton.click();
          this.observer.disconnect();
          console.log('Submit button clicked');
          this.checkForError();
          this.checkForSuccess();
        } else {
          console.log('Submit button is still disabled, retrying...');
        }
      }, 500);
    } else {
      console.log(`${!submitButton ? 'Submit button' : 'Form inputs'} not found yet, retrying...`);
    }
  }

  injectSelectValues(node?: HTMLElement) {
    const selectElement = document.querySelector('select') as HTMLSelectElement;
    selectElement.value = 'North America';
    const changeEvent = new Event('change', { bubbles: true });
    selectElement.dispatchEvent(changeEvent);
    const submitButton = node?.querySelector('button') as HTMLButtonElement;

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.zone.run(() => {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('Mui-disabled');
          if (!submitButton.hasAttribute('disabled')) {
            submitButton.click();
            this.observer.disconnect();
            this.checkForError();
            this.checkForSuccess();
          }
        }, 500);
      });
    });
  }

  simulateUserInput(element: HTMLInputElement, value: string) {
    element.focus();
    element.value = '';
    for (const char of value) {
      const event = new KeyboardEvent('keydown', { bubbles: true, cancelable: true, key: char });
      element.dispatchEvent(event);
      element.value += char;
      const inputEvent = new Event('input', { bubbles: true });
      element.dispatchEvent(inputEvent);
    }
    const changeEvent = new Event('change', { bubbles: true });
    element.dispatchEvent(changeEvent);
    element.blur();
  }

  hideModal(modal?: Node) {
    console.log('Attempting to hide modal...');
    if (modal) {
      this.renderer.setStyle(modal, 'display', 'none');
      return;
    }
  }

  checkForError() {
    const errorInterval = setInterval(() => {
      const errorElement = Array.from(document.querySelectorAll('h4')).find((el) =>
        el.textContent.includes('Error Connecting Account'),
      ) as HTMLElement;
      if (errorElement) {
        const errorMsg1 = errorElement.nextElementSibling?.textContent;
        const errorMsg2 =
          errorElement.nextElementSibling?.nextElementSibling?.nextElementSibling?.nextElementSibling?.textContent;
        this.errorObject = { errorMsg1, errorMsg2 };
        const closeButton =
          this.closeButton ?? (document.querySelector('button.MuiButtonBase-root.MuiIconButton-root') as HTMLElement);
        if (closeButton) {
          this.callForClose(closeButton, this.errorObject, errorInterval);
        }
      } else {
        if (this.modalClosed) {
          clearInterval(errorInterval);
        }
      }
    }, 500);

    setTimeout(() => clearInterval(errorInterval), 10000);
  }

  checkForSuccess() {
    const successInterval = setInterval(() => {
      const successElement = Array.from(document.querySelectorAll('p')).find((el) =>
        el.textContent.includes('You have successfully'),
      ) as HTMLElement;
      if (successElement) {
        const successMsg = successElement.textContent;
        this.successObject = { successMsg };
        const closeButton =
          this.closeButton ?? (document.querySelector('button.MuiButtonBase-root.MuiIconButton-root') as HTMLElement);
        if (closeButton) {
          this.callForClose(closeButton, this.successObject, successInterval);
        }
      } else {
        if (this.modalClosed) {
          clearInterval(successInterval);
        }
      }
    }, 500);

    setTimeout(() => clearInterval(successInterval), 10000);
  }

  callForClose(closeButton, objectToSend: any, intervalToClear: any) {
    this.observer.disconnect();
    this.modalClosed = true;
    this.config.onClose(objectToSend);
    clearInterval(intervalToClear);
    closeButton.click();
    console.log('Modal closed successfully.');
  }
}
